import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAgentNotifications } from '../stores/agent-notifications';
import { useBusConnection } from '../lib/bus-connection';
import { formatRelativeOrAbsolute } from '../lib/datetime';
import styles from './NotificationBell.module.css';

// The notification centre: a bell in the header with the unread count, and a
// panel of the agent's latest notifications (chats and tickets assigned to
// them, chats routed to their department). Clicking one opens it.
export function NotificationBell() {
  const navigate = useNavigate();
  const { items, unread, load, markRead, markAllRead } = useAgentNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const conn = useBusConnection();

  // On mount, and again whenever live updates come back: anything that
  // arrived while disconnected is picked up from the server.
  useEffect(() => {
    if (conn === 'open') void load();
  }, [conn, load]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={styles.root} ref={ref}>
      <button
        type="button"
        className={styles.bell}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        title="Notifications"
      >
        <Bell size={14} aria-hidden />
        {unread > 0 ? <span className={styles.badge}>{unread > 99 ? '99+' : unread}</span> : null}
      </button>
      {open ? (
        <div className={styles.panel} role="dialog" aria-label="Notifications">
          <div className={styles.head}>
            <span>Notifications</span>
            {unread > 0 ? (
              <button type="button" className={styles.markAll} onClick={() => void markAllRead()}>
                Mark all read
              </button>
            ) : null}
          </div>
          {items.length === 0 ? (
            <p className={styles.empty}>Nothing yet. Chats and tickets assigned to you will show up here.</p>
          ) : (
            <ul className={styles.list}>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className={styles.item}
                    data-unread={n.readAt ? undefined : 'true'}
                    onClick={() => {
                      void markRead(n.id);
                      setOpen(false);
                      if (n.link) navigate(n.link);
                    }}
                  >
                    <span className={styles.title}>{n.title}</span>
                    {n.body ? <span className={styles.body}>{n.body}</span> : null}
                    <time className={styles.when} dateTime={new Date(n.createdAt).toISOString()}>
                      {formatRelativeOrAbsolute(n.createdAt)}
                    </time>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
